// screens/Audience.tsx — Chunk AUD-W: the Audience tab's own container,
// same shape as Personal.tsx (B1 of the 01B nav) — a sub-tab strip (Home ·
// Inbox · Week) over the sub-tab's own screen, the sub-tab IN THE URL so a
// reload or back/forward keeps the place.
import { useState } from 'preact/hooks';
import { AUDIENCE_SUBS, routePath, type AudienceSub } from '../routes/route';
import { AudienceHome } from './AudienceHome';
import { AudienceInbox } from './AudienceInbox';
import { AudienceWeek } from './AudienceWeek';
import type { AudienceInboxFilter } from '../lib/api';
import './Audience.css';

export interface AudienceProps {
  tenantId: string;
  sub: AudienceSub;
  weekStart?: string;
  navigate: (path: string) => void;
  onOpenReview: () => void;
  onOpenInboxItem: (id: string) => void;
}

export function Audience({ tenantId, sub, weekStart, navigate, onOpenReview, onOpenInboxItem }: AudienceProps) {
  // The inbox filter is local, not in the hash — the contract's five
  // states plus 'all' are a view over one screen, not a bookmarkable place
  // of their own (unlike the Personal tab's To-dos filter, which the
  // Todos screen already treats as a route for a reason this tab's spec
  // does not ask for).
  const [inboxFilter, setInboxFilter] = useState<AudienceInboxFilter>('all');

  let pane;
  switch (sub) {
    case 'home':
      pane = (
        <AudienceHome
          tenantId={tenantId}
          onOpenReview={onOpenReview}
          onOpenInbox={() => navigate(routePath({ screen: 'audience', sub: 'inbox' }))}
        />
      );
      break;
    case 'inbox':
      pane = (
        <AudienceInbox
          tenantId={tenantId}
          filter={inboxFilter}
          onSelectFilter={setInboxFilter}
          onOpen={onOpenInboxItem}
        />
      );
      break;
    case 'week':
      pane = (
        <AudienceWeek
          tenantId={tenantId}
          start={weekStart}
          onNavigateWeek={(start) => navigate(routePath({ screen: 'audience', sub: 'week', weekStart: start }))}
        />
      );
      break;
  }

  return (
    <div class="audience">
      <nav class="audience__tabs" aria-label="Audience">
        {AUDIENCE_SUBS.map((s) => (
          <a
            key={s.id}
            class={`audience__tab${s.id === sub ? ' is-active' : ''}`}
            href={`#${routePath({ screen: 'audience', sub: s.id })}`}
            aria-current={s.id === sub ? 'page' : undefined}
            onClick={(e) => {
              e.preventDefault();
              navigate(routePath({ screen: 'audience', sub: s.id }));
            }}
          >
            {s.label}
          </a>
        ))}
      </nav>
      <div class="audience__pane">{pane}</div>
    </div>
  );
}
